import React from 'react';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import type { ClaimAnnotation } from '../../types';

interface ClaimAnnotationIndicatorProps {
  annotations: ClaimAnnotation[];
}

interface GroupedAnnotations {
  errors: ClaimAnnotation[];
  warnings: ClaimAnnotation[];
  info: ClaimAnnotation[];
}

/**
 * Component that displays colored icons for claim annotations.
 * Hovering over the icons shows a tooltip with detailed annotation information.
 */
export const ClaimAnnotationIndicator: React.FC<ClaimAnnotationIndicatorProps> = ({ 
  annotations 
}) => {
  // Group annotations by severity
  const grouped: GroupedAnnotations = {
    errors: annotations.filter(a => a.severity === 'error'),
    warnings: annotations.filter(a => a.severity === 'warning'),
    info: annotations.filter(a => a.severity === 'info'),
  };

  // Don't render anything if no annotations
  if (annotations.length === 0) {
    return null;
  }

  // Create the tooltip content
  const tooltipContent = (
    <Box sx={{ p: 1.5, minWidth: 250, maxWidth: 350 }}>
      {/* Header */}
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Validation Results
      </Typography>
      
      {/* Summary counts */}
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        {grouped.errors.length > 0 && (
          <Typography variant="caption" color="error.main" sx={{ fontWeight: 500 }}>
            {grouped.errors.length} Error{grouped.errors.length !== 1 ? 's' : ''}
          </Typography>
        )}
        {grouped.warnings.length > 0 && (
          <Typography variant="caption" color="warning.main" sx={{ fontWeight: 500 }}>
            {grouped.warnings.length} Warning{grouped.warnings.length !== 1 ? 's' : ''}
          </Typography>
        )}
        {grouped.info.length > 0 && (
          <Typography variant="caption" color="info.main" sx={{ fontWeight: 500 }}>
            {grouped.info.length} Info
          </Typography>
        )}
      </Stack>

      <Divider sx={{ my: 1 }} />

      {/* Annotation list grouped by severity */}
      <List dense sx={{ py: 0 }}>
        {/* Errors */}
        {grouped.errors.map((annotation, index) => (
          <ListItem key={`error-${index}`} alignItems="flex-start" sx={{ py: 0.5, px: 0 }}>
            <ListItemIcon sx={{ minWidth: 28, mt: 0.25 }}>
              <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                  {annotation.message}
                </Typography>
              }
              secondary={
                annotation.field || annotation.cms_box ? (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {annotation.field && `Field: ${annotation.field}`}
                    {annotation.field && annotation.cms_box && ' • '}
                    {annotation.cms_box && `Box: ${annotation.cms_box}`}
                  </Typography>
                ) : null
              }
            />
          </ListItem>
        ))}
        
        {/* Warnings */}
        {grouped.warnings.map((annotation, index) => (
          <ListItem key={`warning-${index}`} alignItems="flex-start" sx={{ py: 0.5, px: 0 }}>
            <ListItemIcon sx={{ minWidth: 28, mt: 0.25 }}>
              <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                  {annotation.message}
                </Typography>
              }
              secondary={
                annotation.field || annotation.cms_box ? (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {annotation.field && `Field: ${annotation.field}`}
                    {annotation.field && annotation.cms_box && ' • '}
                    {annotation.cms_box && `Box: ${annotation.cms_box}`}
                  </Typography>
                ) : null
              }
            />
          </ListItem>
        ))}
        
        {/* Info */}
        {grouped.info.map((annotation, index) => (
          <ListItem key={`info-${index}`} alignItems="flex-start" sx={{ py: 0.5, px: 0 }}>
            <ListItemIcon sx={{ minWidth: 28, mt: 0.25 }}>
              <InfoIcon sx={{ fontSize: 16, color: 'info.main' }} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                  {annotation.message}
                </Typography>
              }
              secondary={
                annotation.field || annotation.cms_box ? (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {annotation.field && `Field: ${annotation.field}`}
                    {annotation.field && annotation.cms_box && ' • '}
                    {annotation.cms_box && `Box: ${annotation.cms_box}`}
                  </Typography>
                ) : null
              }
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement="bottom-start"
      enterDelay={200}
      leaveDelay={200}
      disableInteractive={false}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: 3,
            border: 1,
            borderColor: 'divider',
            maxHeight: 400,
            overflowY: 'auto',
            '& .MuiTooltip-arrow': {
              color: 'background.paper',
              '&::before': {
                border: 1,
                borderColor: 'divider',
              },
            },
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          cursor: 'pointer',
          p: 0.5,
          borderRadius: 1,
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        {/* Error icon */}
        {grouped.errors.length > 0 && (
          <ErrorIcon 
            sx={{ 
              fontSize: 18,
              color: 'error.main',
            }} 
          />
        )}

        {/* Warning icon */}
        {grouped.warnings.length > 0 && (
          <WarningIcon 
            sx={{ 
              fontSize: 18,
              color: 'warning.main',
            }} 
          />
        )}

        {/* Info icon */}
        {grouped.info.length > 0 && (
          <InfoIcon 
            sx={{ 
              fontSize: 18,
              color: 'info.main',
            }} 
          />
        )}
      </Box>
    </Tooltip>
  );
};